import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Delete, Check, X } from "lucide-react";

export default function NumpadModal({ open, onClose, onSubmit, categoryName, categoryColor }) {
  const [amount, setAmount] = useState("0");

  const handleNumberClick = (num) => {
    if (amount === "0") {
      setAmount(num);
    } else if (amount.length < 10) {
      setAmount(amount + num);
    }
  };

  const handleDecimalClick = () => {
    if (!amount.includes(".")) {
      setAmount(amount + ".");
    }
  };

  const handleBackspace = () => {
    if (amount.length > 1) {
      setAmount(amount.slice(0, -1));
    } else {
      setAmount("0");
    }
  };

  const handleClear = () => {
    setAmount("0");
  };

  const handleSubmit = () => {
    const value = parseFloat(amount);
    if (value > 0) {
      onSubmit(value);
      setAmount("0");
      onClose();
    }
  };

  const handleClose = () => {
    setAmount("0");
    onClose();
  };

  const displayAmount = () => {
    const num = parseFloat(amount) || 0;
    if (amount.includes(".")) {
      const parts = amount.split(".");
      if (parts[1]?.length === 1) {
        return `$${amount}0`;
      } else if (parts[1]?.length === 0) {
        return `$${amount}00`;
      }
      return `$${amount}`;
    }
    return `$${num.toFixed(2)}`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle 
            className="text-xl font-bold text-white text-center py-2 rounded-lg"
            style={{ backgroundColor: categoryColor }}
          >
            {categoryName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Amount Display */}
          <div className="bg-zinc-900 rounded-xl p-4 text-right">
            <span className="text-4xl font-black text-white font-mono">
              {displayAmount()}
            </span>
          </div>

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2">
            {["7", "8", "9", "4", "5", "6", "1", "2", "3"].map((num) => (
              <Button
                key={num}
                type="button"
                onClick={() => handleNumberClick(num)}
                className="h-16 text-2xl font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-900 rounded-xl"
              >
                {num}
              </Button>
            ))}
            <Button
              type="button"
              onClick={handleDecimalClick}
              className="h-16 text-2xl font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-900 rounded-xl"
            >
              .
            </Button>
            <Button
              type="button"
              onClick={() => handleNumberClick("0")}
              className="h-16 text-2xl font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-900 rounded-xl"
            >
              0
            </Button>
            <Button
              type="button"
              onClick={handleBackspace}
              className="h-16 text-xl font-bold bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-xl"
            >
              <Delete className="w-6 h-6" />
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              onClick={handleClear}
              className="h-14 text-lg font-bold bg-red-100 hover:bg-red-200 text-red-800 rounded-xl"
            >
              <X className="w-5 h-5 mr-2" />
              Clear
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={parseFloat(amount) <= 0}
              className="h-14 text-lg font-bold bg-green-600 hover:bg-green-700 text-white rounded-xl"
              style={{ backgroundColor: categoryColor }}
            >
              <Check className="w-5 h-5 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
